import { useEffect, useMemo, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
  Button,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui'
import { Checkbox } from '../ui/checkbox'
import {
  useCreateRole,
  useUpdateRole,
  type Role,
  type SectionDef,
  type SectionAccess,
} from '../../hooks/useRoles'

interface RoleEditDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Роль для редактирования; null — создание новой. */
  role: Role | null
  sections: SectionDef[]
}

type SectionState = Record<string, { view: boolean; edit: boolean }>

export function RoleEditDialog({
  open,
  onOpenChange,
  role,
  sections,
}: RoleEditDialogProps) {
  const createRole = useCreateRole()
  const updateRole = useUpdateRole()

  const [name, setName] = useState('')
  const [state, setState] = useState<SectionState>({})
  const [error, setError] = useState<string | null>(null)

  // Инициализация формы при открытии/смене роли
  useEffect(() => {
    if (!open) return
    setError(null)
    setName(role?.name ?? '')
    const initial: SectionState = {}
    const src = role?.permissions?.sections ?? {}
    for (const s of sections) {
      const entry: SectionAccess | undefined = src[s.key]
      const edit = entry?.edit === true
      const view = edit || entry?.view === true
      initial[s.key] = { view, edit }
    }
    setState(initial)
  }, [open, role, sections])

  const toggle = (key: string, level: 'view' | 'edit', checked: boolean) => {
    setState((prev) => {
      const cur = prev[key] ?? { view: false, edit: false }
      let next = { ...cur }
      if (level === 'edit') {
        // Редактирование подразумевает просмотр
        next = { edit: checked, view: checked ? true : cur.view }
      } else {
        // Снятие просмотра снимает и редактирование
        next = { view: checked, edit: checked ? cur.edit : false }
      }
      return { ...prev, [key]: next }
    })
  }

  const isSaving = createRole.isPending || updateRole.isPending

  const handleSave = async () => {
    setError(null)
    const trimmed = name.trim()
    if (!trimmed) {
      setError('Введите название роли')
      return
    }

    const outSections: Record<string, SectionAccess> = {}
    for (const [key, v] of Object.entries(state)) {
      if (v.view || v.edit) {
        outSections[key] = { view: v.view || v.edit, edit: v.edit }
      }
    }
    const permissions = { sections: outSections }

    try {
      if (role) {
        await updateRole.mutateAsync({ id: role.id, data: { name: trimmed, permissions } })
      } else {
        await createRole.mutateAsync({ name: trimmed, permissions })
      }
      onOpenChange(false)
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e
          ? String((e as { message?: unknown }).message)
          : 'Не удалось сохранить роль'
      setError(msg)
    }
  }

  const title = useMemo(
    () => (role ? 'Редактирование роли' : 'Создание роли'),
    [role],
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogClose onClose={() => onOpenChange(false)} />
        <DialogHeader>
          <div>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>
              Настройте доступ роли к разделам приложения
            </DialogDescription>
          </div>
        </DialogHeader>

        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Название роли
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Например, Управляющий"
              maxLength={100}
            />
          </div>

          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Права доступа</p>
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Раздел</TableHead>
                    <TableHead className="text-center w-32">Просмотр</TableHead>
                    <TableHead className="text-center w-40">Редактирование</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sections.map((s) => {
                    const cur = state[s.key] ?? { view: false, edit: false }
                    return (
                      <TableRow key={s.key}>
                        <TableCell className="font-medium">{s.label}</TableCell>
                        <TableCell className="text-center">
                          <div className="flex justify-center">
                            <Checkbox
                              checked={cur.view}
                              onCheckedChange={(c) =>
                                toggle(s.key, 'view', c === true)
                              }
                              aria-label={`Просмотр: ${s.label}`}
                            />
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex justify-center">
                            <Checkbox
                              checked={cur.edit}
                              onCheckedChange={(c) =>
                                toggle(s.key, 'edit', c === true)
                              }
                              aria-label={`Редактирование: ${s.label}`}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          </div>

          {error && (
            <p className="text-sm text-red-600" role="alert">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Отмена
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Сохранение…' : 'Сохранить'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
